import React, { useEffect, useRef, useState } from "react";
import PointStartIcon from "../../shared/icons/PointStartIcon.svg";
import PointFinishIcon from "../../shared/icons/PointFinishIcon.svg";
import { MapsData } from "../../../services";
import Loading from "../../shared/Loading/Loading";
import { useScreenSize } from "../../../hooks/useScreenSize";

const OrdersMap = ({
  cityFrom,
  cityTo,
  routeData,
  isLoadingMap,
  addressIdFrom,
  addressIdTo,
  coordinatesFrom,
  coordinatesTo,
  points,
  setRouteDistance,
  setRouteDuration,
}) => {
  const { isMobileView } = useScreenSize();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const YANDEX_API_KEY = process.env.REACT_APP_YMAPS_API_KEY;

  useEffect(() => {
    const hasCityFromCoords = cityFrom?.latitude != null && cityFrom?.longitude != null;
    const hasCityToCoords = cityTo?.latitude != null && cityTo?.longitude != null;

    const hasBothAddressCoords = coordinatesFrom != null && coordinatesTo != null;
    const hasBothCityCoords = hasCityFromCoords && hasCityToCoords;
    const hasValidRoute = hasBothAddressCoords || hasBothCityCoords;

    const getFromCoord = () =>
      hasBothAddressCoords
        ? coordinatesFrom
        : [cityFrom?.latitude, cityFrom?.longitude];

    const getToCoord = () =>
      hasBothAddressCoords
        ? coordinatesTo
        : [cityTo?.latitude, cityTo?.longitude];
      
    const loadYandexMaps = () => {
      if (window.ymaps) {
        window.ymaps.ready(initMap);
      } else {
        const script = document.createElement("script");
        script.src = `https://api-maps.yandex.ru/2.1/?apikey=${YANDEX_API_KEY}&lang=ru_RU`;
        script.onload = () => {
          if (window.ymaps) {
            window.ymaps.ready(initMap);
          }
        };
        document.body.appendChild(script);
      }
    };

    const initMap = () => {
      if (!mapContainerRef.current || mapRef.current) return;

      const map = new window.ymaps.Map(mapContainerRef.current, {
        center: [44.85278, 65.50917],
        zoom: 5,
        controls: ["zoomControl"],
      });

      mapRef.current = map;

      map.controls.remove("geolocationControl");
      map.controls.remove("trafficControl");
      map.controls.remove("typeSelector");
      map.controls.remove("fullscreenControl");
      map.controls.remove("rulerControl");
      map.controls.remove("searchControl");
      map.controls.remove("zoomControl");

      if (map.container && map.container.getElement()) {
        const attribution = map.container
          .getElement()
          .querySelector(".ymaps-2-1-79-copyright");
        if (attribution) {
          attribution.style.display = "none";
        }
      }
    };

    const updateMapWithRoute = () => {
      const map = mapRef.current;
      if (!map) return;

      map.geoObjects.removeAll();

      if (
        !isLoadingMap &&
        hasValidRoute &&
        points.length > 0 &&
        points.every((point) => point.latLng != null)
      ) {
        const referencePoints = [
          getFromCoord(),
          ...points.map((point) => point.latLng),
          getToCoord(),
        ];

        const multiRoute = new window.ymaps.multiRouter.MultiRoute(
          {
            referencePoints,
            params: { routingMode: "truck" },
          },
          {
            boundsAutoApply: true,
            wayPointStartIconLayout: "default#image",
            wayPointFinishIconLayout: "default#image",
            wayPointStartIconImageHref: PointStartIcon,
            wayPointFinishIconImageHref: PointFinishIcon,
            wayPointStartIconImageSize: [30, 42],
            wayPointStartIconImageOffset: [-15, -42],
            wayPointFinishIconImageSize: [30, 42],
            wayPointFinishIconImageOffset: [-15, -42],
            routeStrokeWidth: 2,
            routeActiveStrokeWidth: 4,
            routeActiveStrokeColor: "#0000FF",
          },
        );

        map.geoObjects.add(multiRoute);
        multiRoute.model.events.add("requestsuccess", function () {
          const activeRoute = multiRoute.getActiveRoute();
          if (activeRoute) {
            const distance = activeRoute.properties.get("distance").value;
            const duration = activeRoute.properties.get("duration").value;

            const distanceInKm = (distance / 1000).toFixed(2);
            const formatDuration = (duration) => {
              const hours = Math.floor(duration / 3600).toString().padStart(2, '0');
              const minutes = Math.ceil((duration % 3600) / 60).toString().padStart(2, '0');
              return `${hours}:${minutes}`;
            };
            setRouteDistance(distanceInKm);
            setRouteDuration(formatDuration(duration));
          }
        });
      } else if (routeData && !isLoadingMap) {
        const savedRoute = new window.ymaps.Polyline(
          routeData,
          {},
          {
            strokeColor: "#C5115E",
            strokeWidth: 3,
          },
        );

        const startPlacemark = new window.ymaps.Placemark(
          routeData[0],
          {},
          {
            iconLayout: "default#image",
            iconImageHref: PointStartIcon,
            iconImageSize: [30, 42],
            iconImageOffset: [-15, -42],
          },
        );

        const finishPlacemark = new window.ymaps.Placemark(
          routeData[routeData.length - 1],
          {},
          {
            iconLayout: "default#image",
            iconImageHref: PointFinishIcon,
            iconImageSize: [30, 42],
            iconImageOffset: [-15, -42],
          },
        );

        map.geoObjects.add(savedRoute);
        map.geoObjects.add(startPlacemark);
        map.geoObjects.add(finishPlacemark);

        const routeBounds = savedRoute.geometry.getBounds();
        if (routeBounds) {
          map.setBounds(routeBounds, {
            checkZoomRange: true,
            duration: 500,
            zoomMargin: [0, 0, 0, window.innerWidth / 2],
          });
        }
      } else if (
        !isLoadingMap &&
        hasValidRoute &&
        !routeData
      ) {
        const multiRoute = new window.ymaps.multiRouter.MultiRoute(
          {
            referencePoints: [
              getFromCoord(),
              getToCoord(),
            ],
            params: { routingMode: "truck" },
          },
          {
            boundsAutoApply: true,
            wayPointStartIconLayout: "default#image",
            wayPointFinishIconLayout: "default#image",
            wayPointStartIconImageHref: PointStartIcon,
            wayPointFinishIconImageHref: PointFinishIcon,
            wayPointStartIconImageSize: [30, 42],
            wayPointStartIconImageOffset: [-15, -42],
            wayPointFinishIconImageSize: [30, 42],
            wayPointFinishIconImageOffset: [-15, -42],
            routeStrokeWidth: 2,
            routeActiveStrokeWidth: 4,
            routeActiveStrokeColor: "#0000FF",
          },
        );

        map.geoObjects.add(multiRoute);

        multiRoute.model.events.add("requestsuccess", function () {
          const activeRoute = multiRoute.getActiveRoute();
          if (activeRoute) {
            const distance = activeRoute.properties.get("distance").value;
            const duration = activeRoute.properties.get("duration").value;

            const distanceInKm = (distance / 1000).toFixed(2);
            const formatDuration = (duration) => {
              const hours = Math.floor(duration / 3600).toString().padStart(2, '0');
              const minutes = Math.ceil((duration % 3600) / 60).toString().padStart(2, '0');
              return `${hours}:${minutes}`;
            };
            setRouteDistance(distanceInKm);
            setRouteDuration(formatDuration(duration));
           
            const routePaths = activeRoute
              .getPaths()
              .toArray()
              .map((path) => path.getSegments().toArray());
            const routeCoordinates = routePaths
              .flat(2)
              .map((segment) => segment.geometry.getCoordinates());
            const flattenedCoordinates = routeCoordinates.flat();

            const data = {
              fromCityId: cityFrom?.id,
              toCityId: cityTo?.id,
              fromAddressId: coordinatesFrom ? addressIdFrom : "",
              toAddressId: coordinatesTo ? addressIdTo : "",
              jsonData: JSON.stringify(flattenedCoordinates),
              distance: distanceInKm,
              totalTime: formatDuration(duration),
            };

            MapsData.sendRoute(data);
          }
        });
      }
    };

    loadYandexMaps();
    updateMapWithRoute();
  }, [
    routeData,
    addressIdFrom,
    addressIdTo,
    isLoadingMap,
    JSON.stringify(points.map((point) => point.latLng)),
  ]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        display: isMobileView ? "none" : "block",
      }}
    >
      <div ref={mapContainerRef} style={{ width: "100%", height: "100vh" }} />
    </div>
  );
};

export default OrdersMap;
